import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
// import ReCAPTCHA from 'react-google-recaptcha'; // Commented out for future implementation
import API_CONFIG from '../config/api';

const Verify: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { phoneNumber = '09xxxxxxxxx', from = '/home' } = (location.state as { phoneNumber: string, from: string }) || {};
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // const [captchaToken, setCaptchaToken] = useState<string | null>(null); // Commented out for future implementation
  
  // Check if running on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // API base URL from configuration
  const API_URL = API_CONFIG.baseURL;
  
  const [otpValues, setOtpValues] = useState<string[]>(['', '', '', '']);
  const [timeLeft, setTimeLeft] = useState<number>(120); // 2 minutes in seconds
  
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];
  
  // const recaptchaRef = useRef<any>(null); // Commented out for future implementation

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Timer functionality
  useEffect(() => {
    if (timeLeft <= 0) return;
    
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(timerId);
  }, [timeLeft]);

  // Handle OTP input changes
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
  const pastedData = event.clipboardData.getData("text");
  
  // Ensure pasted input consists only of digits
  if (!/^\d+$/.test(pastedData)) return;

  // Convert pasted data into individual digits
  const digits = pastedData.split("");
  
  if (digits.length === otpValues.length) {
    setOtpValues(digits);

    // Move focus to the last input
    inputRefs[otpValues.length - 1]?.current?.focus();
  }
};

const handleOtpChange = (index: number, value: string) => {
  if (!/^\d*$/.test(value)) return;

  const newOtpValues = [...otpValues];
  newOtpValues[index] = value.slice(-1);
  setOtpValues(newOtpValues);

  // Auto-focus next input if a digit is entered manually
  if (index < otpValues.length - 1 && value.length === 1) {
    setTimeout(() => {
      inputRefs[index + 1].current?.focus();
    }, 10);
  }
};


  // Handle keydown events for backspace navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  // Commented out for future implementation
  // const handleCaptchaChange = (token: string | null) => {
  //   setCaptchaToken(token);
  // };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const otpCode = otpValues.join('');
    if (otpCode.length !== 4) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Commented out for future implementation
      // Execute reCAPTCHA v3 for production
      // let captchaTokenToSend = null;
      // if (!isLocalhost && recaptchaRef.current) {
      //   captchaTokenToSend = await recaptchaRef.current.executeAsync();
      // }
      let captchaTokenToSend = null; // No reCAPTCHA for now
      
      // Connect to API for OTP verification
      const response = await axios.post(`${API_URL}/users/login`, {
        mobile: phoneNumber,
        code: otpCode,
        captchaToken: captchaTokenToSend
      });
      
      if (response.data.success) {
        // Ensure token is stored correctly with Bearer prefix removed (if present)
        const token = response.data.token.startsWith('Bearer ') 
          ? response.data.token.substring(7) 
          : response.data.token;
          
        // Store the token in localStorage for future authenticated requests
        localStorage.setItem('authToken', token);
        localStorage.setItem('userData', JSON.stringify(response.data.data));
        
        // Check if user needs to complete registration
        const userData = response.data.data;
        
        if (!userData.registered) {
          navigate('/complete-registration', { 
            state: { userData, from } 
          });
        } else {
          if(from == null || from == "" || from == "/")
          navigate('/home');
          else
          navigate(from);
        }

      } else {
        setError(response.data.message || 'Verification failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to server');
      // Reset captcha on error - Commented out for future implementation
      // if (recaptchaRef.current) {
      //   recaptchaRef.current.reset();
      //   setCaptchaToken(null);
      // }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit phone number
  const handleEditPhoneNumber = () => {
    navigate('/login');
  };

  // Request a new OTP code
  const handleResendOtp = async () => {
    setIsLoading(true);
    setError('');
    
    // Check if captcha is completed (only for production) - Commented out for future implementation
    // if (!isLocalhost && !captchaToken) {
    //   setError('لطفاً reCAPTCHA را تکمیل کنید');
    //   setIsLoading(false);
    //   return;
    // }
    
    try {
      // Connect to API for resending OTP
      const response = await axios.post(`${API_URL}/users/send-code`, {
        mobile: phoneNumber,
        captchaToken: null // No reCAPTCHA for now
      });
      
      if (response.data.success) {
        setTimeLeft(120); // Reset timer
        setOtpValues(['', '', '', '']);
        inputRefs[0].current?.focus();
      } else {
        setError(response.data.message || 'Failed to resend verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to server');
      // Reset captcha on error - Commented out for future implementation
      // if (recaptchaRef.current) {
      //   recaptchaRef.current.reset();
      //   setCaptchaToken(null);
      // }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white md:bg-slate-50 flex h-dvh">
      <main className="w-full">
        <div className="max-w-[480px] mx-auto select-none flex flex-col md:justify-center md:h-dvh">
          <form className="flex flex-col items-start pt-8 md:p-12 rounded-md md:border-1 border-slate-200 bg-white" onSubmit={handleSubmit}>
            <div className="px-4 md:p-0 w-full">
            <img src="/assets/img/logoRazi.png" alt="Razi Logo" width="42px" height="42px" />
            
            <h1 className="text-lg font-bold text-slate-900 mt-2">تایید شماره موبایل</h1>
            <p className="text-sm text-slate-700 mt-2 mb-4">
              کد 4 رقمی ارسال‌شده به شماره
              <span className="font-bold mx-1 text-slate-900">{phoneNumber}</span>
              را واردکنید.
            </p>

            <div className="bg-slate-50 rounded-md border border-slate-200 py-2 px-4 min-h-[48px] flex items-center gap-4 justify-between w-full">
              <span
                role="button"
                className="text-sm text-slate-600 pl-4 border-l border-slate-200 cursor-pointer"
                onClick={handleEditPhoneNumber}
              >
                ویرایش شماره
              </span>
              <span className="text-sm text-slate-800">{phoneNumber}</span>
            </div>

            <div className="flex flex-col gap-4 mt-4 w-full" dir="ltr">
              <div className="flex justify-between gap-2">
                {[...otpValues].map((value, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    value={otpValues[index]}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste} // Add paste event for handling multiple digit input
                    pattern="[0-9]*"
                    inputMode="numeric"
                    min="0"
                    max="9"
                    maxLength={1}
                    autoComplete="one-time-code"
                    type="text" // `tel` is fine but `text` improves cross-browser behavior
                    aria-label={`OTP digit ${index + 1} of ${otpValues.length}`}
                    title={`OTP digit ${index + 1}`}
                    placeholder=""
                    className="border border-slate-200 rounded-md h-[48px] w-[48px] flex-1 text-center text-2xl font-bold focus:border-[#00bcd4] focus:outline-none"
                  />

                ))}
                <div className="border border-slate-200 bg-slate-50 rounded-md h-[48px] w-[48px] flex-1 flex items-center justify-center text-slate-600 text-sm py-2">
                {formatTime(timeLeft)}
              </div>
              </div>
              
              
            </div>

            {/* reCAPTCHA - Commented out for future implementation */}
            {/* {!isLocalhost && (
              <div className="flex justify-center mt-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                    sitekey="6Lc7rcYrAAAAAI1b9LCiEuVnQAOEBYJ_ymEnhnx9"
                  onChange={handleCaptchaChange}
                  theme="light"
                  size="invisible"
                />
              </div>
            )} */}

            {error && (
              <p className="text-red-500 text-sm mb-4">{error}</p>
            )}
             {timeLeft <= 0 && (
              <button
                type="button"
                className="text-blue-700 text-sm cursor-pointer mt-2"
                onClick={handleResendOtp}
                disabled={isLoading}
              >
                {isLoading ? 'در حال ارسال...' : 'ارسال مجدد کد'}
              </button>
            )}
            </div>
            <div className="fixed right-0 left-0 bottom-0 p-4 border-t-1 border-slate-200 md:relative md:p-0 md:border-0 md:mt-8 md:w-full z-90">
              <button
                type="submit"
                className="w-full bg-[#00bcd4] hover:bg-[#00acc1] text-white py-2 px-4 rounded-lg text-md min-h-[48px] cursor-pointer bg-[#1a237e]"
                disabled={otpValues.some(v => !v) || isLoading}
              >
                {isLoading ? 'در حال بررسی...' : 'ورود'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Verify;
